import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus,
  Search,
  Calendar,
  ChevronDown,
  Check,
  User,
  Clock,
  MapPin,
  AlertTriangle,
  FileText,
  PanelRightClose,
  PhoneCall,
  PhoneMissed,
  ShieldAlert,
  Edit2,
} from "lucide-react";
import api from "../../connector/api";
import DisciplinaryReferralModal from "../components/DisciplinaryReferralModal";

/* Custom Term Filter Dropdown Component */
const CustomTermDropdown = ({ terms, selectedTerm, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Helper to format school year term display name
  const getTermLabel = (t) => {
    if (!t) return "";
    if (t.id === "all") return "All Terms";

    // Extract nested names from Django SerializerMethodField
    const termName = t.term_detail?.name || t.term?.name || t.name || t.term_name;
    const yearName = t.school_year_detail?.name || t.school_year?.name || t.school_year_name;

    if (yearName && termName) {
      return `${yearName} - ${termName}`;
    }

    return termName || yearName || `Term ${t.id}`;
  };

  const selectedOption =
    selectedTerm === "all"
      ? { id: "all", name: "All Terms" }
      : terms.find((t) => String(t.id) === String(selectedTerm));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative shrink-0" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="h-8 border border-zinc-200 rounded-lg px-2.5 text-xs font-medium bg-zinc-50 hover:bg-zinc-100 text-zinc-800 flex items-center gap-2 focus:outline-none focus:border-blue-500 transition-colors cursor-pointer"
      >
        <Calendar className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
        <span className="truncate max-w-[200px]">
          {selectedOption ? getTermLabel(selectedOption) : "Select Term"}
        </span>
        <ChevronDown className="w-3.5 h-3.5 text-zinc-400 shrink-0 ml-1" />
      </button>

      {isOpen && (
        <div className="absolute right-0 z-20 w-64 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-56 overflow-y-auto py-1 text-xs [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-zinc-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-zinc-400">
          <button
            type="button"
            onClick={() => {
              onChange("all");
              setIsOpen(false);
            }}
            className={`w-full text-left px-3 py-1.5 flex items-center justify-between hover:bg-zinc-100 transition-colors cursor-pointer ${
              selectedTerm === "all"
                ? "bg-blue-50 text-blue-700 font-semibold"
                : "text-zinc-700"
            }`}
          >
            <span>All Terms</span>
            {selectedTerm === "all" && (
              <Check className="w-3.5 h-3.5 text-blue-600 shrink-0" />
            )}
          </button>
          {terms.map((t) => {
            const isSelected = String(selectedTerm) === String(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  onChange(t.id);
                  setIsOpen(false);
                }}
                className={`w-full text-left px-3 py-1.5 flex items-center justify-between hover:bg-zinc-100 transition-colors cursor-pointer ${
                  isSelected
                    ? "bg-blue-50 text-blue-700 font-semibold"
                    : "text-zinc-700"
                }`}
              >
                <span className="truncate">{getTermLabel(t)}</span>
                {isSelected && (
                  <Check className="w-3.5 h-3.5 text-blue-600 shrink-0 ml-2" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* Independent Height Student Card Component */
const DisciplinaryCard = ({ student, onLogIncident, onEditIncident }) => {
  const records = student.disciplinary_records || [];
  const displayName =
    student.name || `${student.last_name || ""}, ${student.first_name || ""}`.trim();

  return (
    <div className="bg-white border border-zinc-200 rounded-lg shadow-2xs overflow-hidden hover:border-zinc-300 transition-all">
      {/* Student Card Header */}
      <div className="p-3.5 border-b border-zinc-100 bg-zinc-50/60 flex items-center justify-between">
        <div className="flex items-center gap-2.5 overflow-hidden">
          <div className="w-7 h-7 rounded-full bg-zinc-200 border border-zinc-300 flex items-center justify-center shrink-0">
            <User className="w-4 h-4 text-zinc-600" />
          </div>
          <div className="truncate">
            <h3 className="font-bold text-zinc-900 text-xs truncate">
              {displayName}
            </h3>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
            {records.length} {records.length === 1 ? "Incident" : "Incidents"}
          </span>
          <button
            type="button"
            onClick={() => onLogIncident(student)}
            className="h-7 px-2.5 bg-blue-50 hover:bg-blue-100 font-mono text-blue-700 rounded-lg text-[11px] font-semibold flex items-center gap-1 transition-colors cursor-pointer"
          >
            <Plus className="w-3 h-3" />
            <span>Log</span>
          </button>
        </div>
      </div>

      {/* Dynamic Height Records List */}
      <div className="divide-y divide-zinc-100">
        {records.map((rec) => {
          const isMajor = rec.infraction_level === "MAJOR";

          return (
            <div key={rec.id} className="p-3 space-y-2 hover:bg-zinc-50/50 transition-colors group relative">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border ${
                      isMajor
                        ? "bg-rose-50 text-rose-700 border-rose-200"
                        : "bg-amber-50 text-amber-700 border-amber-200"
                    }`}
                  >
                    <AlertTriangle className="w-3 h-3 shrink-0" />
                    {rec.infraction_level || "INCIDENT"}
                  </span>

                  {/* Parent Contact Status Badge */}
                  <span
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-semibold border ${
                      rec.parent_contacted
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-zinc-100 text-zinc-500 border-zinc-200"
                    }`}
                  >
                    {rec.parent_contacted ? (
                      <>
                        <PhoneCall className="w-2.5 h-2.5 text-emerald-600 shrink-0" />
                        <span>Parent Contacted</span>
                      </>
                    ) : (
                      <>
                        <PhoneMissed className="w-2.5 h-2.5 text-zinc-400 shrink-0" />
                        <span>Not Contacted</span>
                      </>
                    )}
                  </span>
                </div>

                <div className="flex items-center gap-2 text-[10px] text-zinc-400">
                  {rec.date_of_incident && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-zinc-400" />
                      {new Date(rec.date_of_incident).toLocaleDateString()}
                    </span>
                  )}
                  
                  {/* Edit Button */}
                  <button
                    type="button"
                    onClick={() => onEditIncident(student, rec)}
                    className="ml-1 inline-flex items-center font-mono gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-800 cursor-pointer"
                  >
                    <span>Edit</span>
                  </button>
                </div>
              </div>

              {rec.incident_description && (
                <div className="flex items-start gap-1.5 text-xs text-zinc-700">
                  <FileText className="w-3.5 h-3.5 text-zinc-400 shrink-0 mt-0.5" />
                  <p className="text-[11px] leading-relaxed break-words">
                    {rec.incident_description}
                  </p>
                </div>
              )}

              {/* Location & Intervention Details */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-zinc-500 pt-1 border-t border-zinc-100/70">
                {rec.location && (
                  <div className="flex items-center gap-1 text-zinc-400">
                    <MapPin className="w-3 h-3 shrink-0" />
                    <span>{rec.location}</span>
                  </div>
                )}

                {/* Intervention Type & Date display */}
                {(rec.intervention_type || rec.intervention_date) && (
                  <div className="flex items-center gap-1.5 text-emerald-700 font-medium bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                    <ShieldAlert className="w-3 h-3 text-emerald-600 shrink-0" />
                    <span>
                      {rec.intervention_type || "Intervention"}:
                    </span>
                    {rec.intervention_date && (
                      <span className="text-[9.5px]">
                        {new Date(rec.intervention_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


const BehaviorTab = ({
  activeClass,
  activeStudents = [],
  selectedSubjectId,
  isRightSidebarOpen,
  setIsRightSidebarOpen,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTerm, setSelectedTerm] = useState("all");
  const [terms, setTerms] = useState([]);

  // Referral state from Django API
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStudentForModal, setSelectedStudentForModal] = useState(null);
  const [selectedRecordForModal, setSelectedRecordForModal] = useState(null);

  // Fetch available terms from year-terms endpoint
  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const response = await api.get("year-terms/");
        const data = response.data.results || response.data;
        setTerms(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to load terms for behavior filter:", err);
      }
    };
    fetchTerms();
  }, []);

  // Fetch referrals from DisciplinaryReferralViewSet
  const fetchReferrals = useCallback(async () => {
    if (!activeClass?.id) return;
    setLoading(true);
    setError(null);

    try {
      const response = await api.get("discipline-referrals/", {
        params: {
          classroom: activeClass.id,
        },
      });
      const data = response.data.results || response.data || [];
      setReferrals(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch disciplinary referrals:", err);
      setError("Failed to load disciplinary records.");
    } finally {
      setLoading(false);
    }
  }, [activeClass?.id]);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  // Combine activeStudents with fetched backend referrals
  const studentsWithReferrals = activeStudents
  .map((student) => {
    const studentIdStr = String(student.id || student.student_id);

    const studentReferrals = referrals.filter((ref) => {
      const refStudentId = String(
        typeof ref.student === "object" ? ref.student?.id : ref.student
      );
      return refStudentId === studentIdStr;
    });

    return {
      ...student,
      disciplinary_records: studentReferrals,
    };
  })
  .filter((st) => {
    const records = st.disciplinary_records;
    if (!records || records.length === 0) return false;

    const studentName = `${st.first_name || ""} ${st.last_name || ""} ${
      st.name || ""
    }`.toLowerCase();
    const matchesSearch = studentName.includes(searchQuery.toLowerCase());

    const matchesTerm =
      selectedTerm === "all" ||
      records.some((rec) => {
        const termId =
          typeof rec.school_year_term === "object"
            ? rec.school_year_term?.id
            : rec.school_year_term;
        return String(termId) === String(selectedTerm);
      });

    return matchesSearch && matchesTerm;
  })
  // 💡 Sorts students alphabetically by last_name (or first_name if last_name is empty)
  .sort((a, b) => {
    const lastNameA = a.last_name || a.first_name || "";
    const lastNameB = b.last_name || b.first_name || "";

    return lastNameA.localeCompare(lastNameB, undefined, {
      sensitivity: "base",
      numeric: true,
    });
  });

  const handleOpenModal = (student = null) => {
    setSelectedStudentForModal(student);
    setSelectedRecordForModal(null);
    setIsModalOpen(true);
  };

  const handleEditIncident = (student, record) => {
    setSelectedStudentForModal(student);
    setSelectedRecordForModal(record);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedStudentForModal(null);
    setSelectedRecordForModal(null);
  };

  const handleModalSuccess = () => {
    fetchReferrals();
  };

  return (
    <div className="flex-1 p-4 space-y-4 w-full flex flex-col h-auto min-h-0 bg-zinc-100">
      {/* Control Bar: Search Input, Term Filter, Log Note & Summary Sidebar Toggle */}
      <div className="flex items-center gap-3 bg-white border border-zinc-200 rounded-lg p-2.5 shadow-2xs">
        <div className="flex-1 flex items-center gap-2 border-r border-zinc-200 pr-3">
          <Search className="w-4 h-4 text-zinc-400 shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search students with records..."
            className="w-full bg-transparent text-xs text-zinc-800 placeholder-zinc-400 focus:outline-none"
          />
        </div>

        {/* Term Dropdown Filter */}
        <CustomTermDropdown
          terms={terms}
          selectedTerm={selectedTerm}
          onChange={setSelectedTerm}
        />

        {/* Log Behavior Note Button */}
        <button
          type="button"
          onClick={() => handleOpenModal(null)}
          className="h-8 px-3 bg-[#002855] hover:bg-blue-900 font-mono text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer shrink-0"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Log Behavior</span>
        </button>

        {typeof setIsRightSidebarOpen === "function" && !isRightSidebarOpen && (
          <button
            onClick={() => setIsRightSidebarOpen(true)}
            className="h-8 px-3 border border-zinc-200 hover:bg-zinc-100 font-mono rounded-lg text-zinc-700 transition-colors flex items-center gap-1.5 text-xs font-semibold shrink-0"
            title="Open Sidebar"
          >
            <PanelRightClose className="w-4 h-4 text-zinc-600" />
            <span>Summary</span>
          </button>
        )}
      </div>

      {/* Cards Grid Container with custom scrollbar */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-zinc-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-zinc-400">
        {loading ? (
          <div className="p-12 text-center text-zinc-400 text-xs bg-white border border-zinc-200 rounded-lg">
            Loading disciplinary records...
          </div>
        ) : error ? (
          <div className="p-12 text-center text-rose-500 text-xs bg-white border border-zinc-200 rounded-lg">
            {error}
          </div>
        ) : studentsWithReferrals.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 items-start">
            {studentsWithReferrals.map((student, idx) => (
              <DisciplinaryCard
                key={student.id || idx}
                student={student}
                onLogIncident={handleOpenModal}
                onEditIncident={handleEditIncident}
              />
            ))}
          </div>
        ) : (
          <div className="p-12 text-center text-zinc-400 text-xs bg-white border border-zinc-200 rounded-lg">
            {searchQuery || selectedTerm !== "all"
              ? "No student disciplinary records match your criteria."
              : "No active student disciplinary records found for this classroom."}
          </div>
        )}
      </div>

      {/* Disciplinary Referral Modal */}
      {isModalOpen && (
        <DisciplinaryReferralModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          selectedStudent={selectedStudentForModal}
          selectedRecord={selectedRecordForModal}
          students={activeStudents}
          subjectAssignmentId={selectedSubjectId}
          classId={activeClass?.id}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default BehaviorTab;